import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import Reports from "@/pages/Reports";
import ReportBuilder from "@/pages/ReportBuilder";
import TimesheetReports from "@/pages/TimesheetReports";

export default function ReportsHub() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const role = user?.role;
  const canBuilder = ["admin", "manager", "timesheet_admin"].includes(role);
  const canTs = role === "admin" || role === "timesheet_admin" || ["manager", "admin"].includes(user?.timesheet_role);

  const tabs = useMemo(() => {
    const t = [{ value: "reports", label: "Reports" }];
    if (canBuilder) t.push({ value: "builder", label: "Report Builder" });
    if (canTs) t.push({ value: "timesheet", label: "Timesheet-Reports" });
    return t;
  }, [canBuilder, canTs]);

  const requested = params.get("view");
  const active = tabs.some((t) => t.value === requested) ? requested : "reports";
  const setActive = (v) => {
    const p = new URLSearchParams(params);
    p.set("view", v);
    setParams(p, { replace: true });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Shareable link copied — opens this exact tab, date range & filters");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div data-testid="reports-hub">
      <Tabs value={active} onValueChange={setActive} className="w-full">
        <div className="flex items-center justify-between gap-3 px-6 pt-6">
          {tabs.length > 1 ? (
            <TabsList data-testid="reports-hub-tabs">
              {tabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value} data-testid={`reports-hub-tab-${t.value}`}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          ) : <div />}
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="reports-copy-link"
            className="rounded-sm font-mono text-[10px] uppercase tracking-widest shrink-0">
            <LinkSimple size={14} className="mr-1.5" /> Copy link
          </Button>
        </div>
        <TabsContent value="reports" className="mt-0">
          <Reports />
        </TabsContent>
        {canBuilder && (
          <TabsContent value="builder" className="mt-0">
            <ReportBuilder embedded />
          </TabsContent>
        )}
        {canTs && (
          <TabsContent value="timesheet" className="mt-0">
            <TimesheetReports />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
